import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  Input,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { assertIsUUID, type UUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import { ContentDetailsGlobalStore } from '@bizzdesign/data-access-content-details';
import { type HighchartsData } from '@bizzdesign/data-access-highcharts';
import {
  type ElementSource,
  type HighchartsContent,
  type ToolContainer,
  type ToolContentUpdate,
  type ToolContentUpdateNotice,
  type ToolEvent,
} from '@bizzdesign/shared/models';
import { LayoutContextService } from '@bizzdesign/shared/services';
import {
  resetActiveChartStyles,
  svgToBase64,
} from '@bizzdesign/shared/visualization-templates';
import { LoaderComponent } from '@bizzdesign/ui-loader';
import { HighchartsChartComponent } from 'highcharts-angular';
import type * as Highcharts from 'highcharts/highcharts-gantt';
import { take } from 'rxjs';
import { HighchartsDrawersContainerComponent } from '../highcharts-drawers-container/highcharts-drawers-container.component';
import { HighchartsLocalStore } from '../../state/highcharts/highcharts.store';
import { CallToActionComponent } from '@bizzdesign/ui-call-to-action';

@Component({
  selector: 'bd-highcharts-container',
  templateUrl: './highcharts-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    NgClass,
    HighchartsChartComponent,
    LoaderComponent,
    HighchartsDrawersContainerComponent,
    CallToActionComponent,
  ],
  providers: [HighchartsLocalStore],
  styleUrls: ['./highcharts-container.component.scss'],
})
export class HighchartsContainerComponent
  implements ToolContainer<HighchartsContent>
{
  readonly layoutContextService = inject(LayoutContextService);
  readonly contentDetailsGlobalStore = inject(ContentDetailsGlobalStore);
  readonly highchartsStore: HighchartsLocalStore = inject(HighchartsLocalStore);
  readonly isOverlayOutlet =
    inject(ActivatedRoute).snapshot.outlet === 'overlay';

  @Input() config: object | undefined;
  @Input() previewMode = false;

  @Input() onToolEvent: ((event: ToolEvent) => void) | undefined;

  private _data: HighchartsData | undefined;
  @Input() set data(data: HighchartsData | undefined) {
    this._data = data;
    if (data?.content$) {
      data.content$
        .pipe(take(1))
        .subscribe((content) =>
          this.loadChartOptions(content, data?.sourceQuery),
        );
    }
  }
  get data(): HighchartsData | undefined {
    return this._data;
  }

  @Input() onContentUpdateNotice:
    | ((notice: ToolContentUpdateNotice<HighchartsContent>) => void)
    | undefined;

  @Input() onElementIdsSelectionChange:
    | ((elementIds: UUID[]) => void)
    | undefined;

  @Input() onObjectIdsInUseChange: ((objectIds: UUID[]) => void) | undefined;

  private async getContentUpdate(): Promise<
    ToolContentUpdate<HighchartsContent>
  > {
    return this.highchartsStore.getContentUpdate();
  }
  lastChartInstance: Highcharts.Chart;

  constructor() {
    effect(() => {
      if (
        (this.highchartsStore.loadingState() === 'loaded' &&
          this.highchartsStore.needsPreview()) ||
        this.highchartsStore.loadingState() === 'changed'
      ) {
        this.emitContentUpdateNotice();
      }
    });

    effect(() => {
      if (this.highchartsStore.loadingState() === 'loaded') {
        this.onObjectIdsInUseChange?.(
          this.highchartsStore.objectsList().map((o) => o.id),
        );
      }
    });
  }

  emitContentUpdateNotice(): void {
    this.onContentUpdateNotice?.({
      getContentUpdate: () => this.getContentUpdate(),
    });
  }

  private loadChartOptions(
    content: HighchartsContent | undefined,
    sourceQuery: ElementSource | undefined,
  ): void {
    if (content?.dataset && content?.configuration) {
      this.highchartsStore.loadChartOptions({
        input: {
          dataset: content.dataset,
          configuration: content.configuration,
          needsPreview: !!content?.needsPreview,
          previewMode: this.previewMode,
          sourceQuery: sourceQuery,
        },
      });
    }
  }

  onChartInstance(chart: Highcharts.Chart): void {
    if (chart && !chart.renderer.forExport) {
      this.lastChartInstance = chart;
      const exporting = chart.exporting;
      const previewCallback = () => svgToBase64(exporting.getSVG() as string);
      this.highchartsStore.setPreviewCallback(previewCallback);
    }
  }

  onContentDetailsClose() {
    resetActiveChartStyles(this.lastChartInstance);
  }

  onContentItemAddTo(objectId: string): void {
    assertIsUUID(objectId);
    this.onToolEvent?.({
      key: 'objectAddTo',
      payload: objectId,
    });
  }

  onContentRefresh() {
    resetActiveChartStyles(this.lastChartInstance);
    this.highchartsStore.refreshChartOptions();
  }
}
